<script setup lang="ts">
import GaiaText from '@/components/design/base/GaiaText.vue';
import {
  gaiaAmber,
  gaiaFontSize,
  gaiaNeutral,
  gaiaRiskTint,
  gaiaStatus,
} from '@/components/design/themes/tokens';

/**
 * One result in the Playbooks pane — an extraction, a suggestion, a clause
 * check or a finding. A bordered card whose edge carries its state, the same
 * way the web's run detail tints an accepted or declined suggestion; the card
 * body is whatever the pane slots in (redline, class chip, reasoning).
 */
const props = defineProps<{
  title: string;
  /** Drives the border tint; `null` for cards that carry no accept/decline state. */
  state?: 'pending' | 'accepted' | 'declined' | 'closed' | null;
  failed?: boolean;
  /** Clicking a focusable card reveals its text in the Word document. */
  focusable?: boolean;
}>();

const emit = defineEmits<{
  activate: [];
}>();

function activate(): void {
  if (props.focusable) {
    emit('activate');
  }
}

// --- chrome ------------------------------------------------------------------
const border = gaiaNeutral.borderCard;
const borderHover = gaiaNeutral.borderHover;
const surface = gaiaNeutral.surface;
const acceptedBorder = gaiaRiskTint.successBorder;
const declinedBorder = gaiaStatus.alertBorder;
const failedBorder = gaiaAmber.border;
const failedSurface = gaiaAmber.surface;
const reasoningColor = gaiaNeutral.textTertiary;
const reasoningSize = gaiaFontSize.dense;
</script>

<template>
  <div
    class="result-card"
    :class="[
      state ? `result-card--${state}` : null,
      failed ? 'result-card--failed' : null,
      focusable ? 'result-card--focusable' : null,
    ]"
    :role="focusable ? 'button' : undefined"
    :tabindex="focusable ? 0 : undefined"
    :title="focusable ? 'Show in document' : undefined"
    @click="activate"
    @keydown.enter.prevent="activate"
    @keydown.space.prevent="activate"
  >
    <n-flex align="flex-start" justify="space-between" :size="10" :wrap="false">
      <gaia-text
        size="dense"
        weight="semibold"
        tone="ink"
        :style="{ minWidth: 0, lineHeight: 1.35, overflowWrap: 'anywhere' }"
      >
        {{ title }}
      </gaia-text>
      <n-flex
        v-if="$slots['header-extra']"
        align="center"
        :size="6"
        :wrap="false"
        :style="{ flex: 'none' }"
        @click.stop
      >
        <slot name="header-extra" />
      </n-flex>
    </n-flex>

    <n-flex
      v-if="$slots.meta"
      align="center"
      :size="6"
      :style="{ marginTop: '6px', width: '100%' }"
      @click.stop
    >
      <slot name="meta" />
    </n-flex>

    <slot />

    <!-- The model's reasoning, folded by default: it is the answer to "why?",
         and most cards are read for their what. -->
    <details v-if="$slots.reasoning" class="reasoning" @click.stop>
      <summary>Reasoning</summary>
      <div class="reasoning__body"><slot name="reasoning" /></div>
    </details>
  </div>
</template>

<style scoped>
/* State on the card's edge, not on its text: the tint says what happened to
   the suggestion without competing with what it says. */
.result-card {
  box-sizing: border-box;
  min-width: 0;
  padding: 12px;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: v-bind(surface);
  transition:
    border-color 0.12s ease,
    opacity 0.12s ease;
}

.result-card--focusable {
  cursor: pointer;
}

.result-card--focusable:hover,
.result-card--focusable:focus-visible {
  border-color: v-bind(borderHover);
  outline: none;
}

.result-card--accepted {
  border-color: v-bind(acceptedBorder);
}

.result-card--declined {
  border-color: v-bind(declinedBorder);
  opacity: 0.82;
}

.result-card--closed {
  opacity: 0.7;
}

.result-card--failed {
  border-color: v-bind(failedBorder);
  background: v-bind(failedSurface);
}

/* A native disclosure keeps the fold accessible for free; only its type is
   ours. `:deep` reaches the paragraphs the panes slot in. */
.reasoning {
  margin-top: 12px;
  font-size: v-bind(reasoningSize);
  color: v-bind(reasoningColor);
}

.reasoning summary {
  cursor: pointer;
  line-height: 1.3;
}

.reasoning__body :deep(p) {
  margin: 8px 0 0;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
</style>
